<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2>Wallet Approvals</h2>
          <p class="text-muted mb-0">
            Pending credit and refund requests, oldest first, plus any request stranded in
            EXECUTING or APPROVED &mdash; a crash between the claim and the executor call, or between
            approval and the claim. Resuming is safe: every executor is idempotent on the request's own
            id, so it can never move money twice.
          </p>
        </div>
      </div>
    </div>

    <div class="col-xl-12 xl-100 box-col-6">
      <div class="card">
        <div class="card-body social-list filter-cards-view">
          <div v-if="dataLoaded">
            <DataTable
              :ajax="{ url: `/api/datatable/wallet-adjustment-queue` }"
              ref="queueDataTable" class="table-responsive display" :columns="queueColumns" />
          </div>
          <div v-else class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-2"></span> Loading...
          </div>
        </div>
      </div>
    </div>

    <!-- Reject reason modal -->
    <div class="modal fade" id="wallet-reject-modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reject Request</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <label class="form-label">Reason <span class="text-danger">*</span></label>
            <textarea class="form-control" rows="3" v-model="rejectReason" required></textarea>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" :disabled="isSubmitting || !rejectReason.trim()" @click="confirmReject">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ isSubmitting ? 'Rejecting...' : 'Reject Request' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import { Modal } from 'bootstrap';
import { minorCurrency, walletAdjustmentRequestStatus, zonedDateTime } from '../utility/DatatableRenderer';
import { showToast, notificationType } from '../utility/toastNotificationUtility';

DataTable.use(DataTablesCore);

const queueDataTable = ref(null);
const dataLoaded = ref(false);
const rejectReason = ref('');
const rejectingRequestId = ref(null);
const isSubmitting = ref(false);
// Rows with an action in flight, keyed by request id. DataTables re-renders actionsRender's raw HTML
// on every ajax reload rather than tracking real Vue elements per row, so double-submission is guarded
// here (checked before the fetch, disabled via the DOM element clicked) instead of via a per-row
// reactive ref the way the modal components in this feature do.
const rowsInFlight = new Set();

const reloadTable = () => {
  if (queueDataTable.value?.dt) queueDataTable.value.dt.ajax.reload(null, false);
};

const runRowAction = async (requestId, buttonEl, path, successMessage, failureMessage) => {
  if (rowsInFlight.has(requestId)) return;
  rowsInFlight.add(requestId);
  const originalText = buttonEl ? buttonEl.textContent : null;
  if (buttonEl) {
    buttonEl.disabled = true;
    buttonEl.innerHTML = '<span class="spinner-border spinner-border-sm"></span>';
  }
  try {
    const response = await fetch(`/api/admin/wallet-adjustments/${requestId}/${path}`, { method: 'POST' });
    if (!response.ok) {
      const message = await response.text();
      showToast({ title: 'Error', body: message || failureMessage, type: notificationType.ERROR, delay: 6000 });
      return;
    }
    showToast({ title: 'Success', body: successMessage, type: notificationType.SUCCESS });
    reloadTable();
  } catch (error) {
    console.error(`Error on wallet adjustment request ${path}:`, error);
    showToast({ title: 'Oops..', body: 'Encountered an internal error. Please contact support.', type: notificationType.ERROR, delay: 7000 });
  } finally {
    rowsInFlight.delete(requestId);
    if (buttonEl && document.body.contains(buttonEl)) {
      buttonEl.disabled = false;
      buttonEl.textContent = originalText;
    }
  }
};

const approve = (requestId, buttonEl) =>
  runRowAction(requestId, buttonEl, 'approve', 'Request approved and execution attempted.', 'Unable to approve this request.');

const resume = (requestId, buttonEl) =>
  runRowAction(requestId, buttonEl, 'resume', 'Execution resumed.', 'Unable to resume this request.');

const openReject = (requestId) => {
  rejectingRequestId.value = requestId;
  rejectReason.value = '';
  const modalEl = document.getElementById('wallet-reject-modal');
  (Modal.getInstance(modalEl) || new Modal(modalEl)).show();
};

const confirmReject = async () => {
  if (!rejectReason.value.trim() || isSubmitting.value || !rejectingRequestId.value) return;
  isSubmitting.value = true;
  try {
    const response = await fetch(`/api/admin/wallet-adjustments/${rejectingRequestId.value}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: rejectReason.value.trim() })
    });
    if (!response.ok) {
      const message = await response.text();
      showToast({ title: 'Error', body: message || 'Unable to reject this request.', type: notificationType.ERROR, delay: 6000 });
      return;
    }
    const modalEl = document.getElementById('wallet-reject-modal');
    (Modal.getInstance(modalEl) || new Modal(modalEl)).hide();
    showToast({ title: 'Success', body: 'Request rejected.', type: notificationType.SUCCESS });
    reloadTable();
  } catch (error) {
    console.error('Error rejecting wallet adjustment request:', error);
    showToast({ title: 'Oops..', body: 'Encountered an internal error. Please contact support.', type: notificationType.ERROR, delay: 7000 });
  } finally {
    isSubmitting.value = false;
  }
};

// Event delegation, matching the DatatableRenderer.js editAndDeleteCTA convention: DataTables
// re-renders rows on every ajax reload, so listeners are bound once on the document rather than
// per-render.
$(document).on('click', '.wallet-approve-btn', function () {
  approve($(this).data('id'), this);
});
$(document).on('click', '.wallet-reject-btn', function () {
  openReject($(this).data('id'));
});
$(document).on('click', '.wallet-resume-btn', function () {
  resume($(this).data('id'), this);
});

const actionsRender = (data, type, row) => {
  if (row.status === 'PENDING_APPROVAL') {
    return `<button class="btn btn-sm btn-success wallet-approve-btn" data-id="${row.id}">Approve</button>
            <button class="btn btn-sm btn-outline-danger wallet-reject-btn" data-id="${row.id}">Reject</button>`;
  }
  if (row.status === 'EXECUTING' || row.status === 'APPROVED') {
    return `<span class="badge bg-danger me-2">Stranded</span>
            <button class="btn btn-sm btn-outline-warning wallet-resume-btn" data-id="${row.id}">Resume</button>`;
  }
  return '';
};

const queueColumns = [
  { data: 'createdOn', title: 'Requested', render: zonedDateTime },
  { data: 'kind', title: 'Kind' },
  {
    data: 'walletId', title: 'Wallet', render: (data) =>
      `<a href="/admin/wallets/${data}">${(data || '').slice(0, 8)}&hellip;</a>`
  },
  { data: 'amountMinor', title: 'Amount', render: minorCurrency },
  { data: 'status', title: 'Status', render: walletAdjustmentRequestStatus },
  { data: 'reason', title: 'Reason' },
  { title: 'Actions', orderable: false, searchable: false, render: actionsRender }
];

onMounted(() => {
  dataLoaded.value = true;
});
</script>
